import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight, Gamepad2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Thumbs, Mousewheel, Scrollbar } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
import styled, { keyframes } from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import { useGameEngine, getGameImage } from 'hooks/useGameEngine';

const GamesContainer = styled.div`
  margin-top: 32px !important;
  margin-bottom: 24px !important;
  background-color: transparent;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 0 4px;
`;

const TitleText = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  text-transform: capitalize;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AllButtonTopTitleContainer = styled.span`
  color: #94a6b5;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.2s;
  text-transform: capitalize;

  &:hover {
    color: #ffffff;
  }
`;

const PaginationButton = styled.div`
  background-color: #21272b;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #94a6b5;
  z-index: 1;
  width: 36px;
  height: 36px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: #2f4553;
    color: #ffffff;
  }
`;

const GridItem = styled.div`
  background-color: #292d2e;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: transform 0.2s ease, border-color 0.2s ease;
  aspect-ratio: 3 / 4;

  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }

  .swiper-slide-img {
    width: 100%;
    height: 100%;
    object-fit: fill;
    transition: transform 0.5s ease;
  }

  &:hover .swiper-slide-img {
    transform: scale(1.08);
  }

  .play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 20px;
    color: white;
    opacity: 0;
    padding: 13px;
    text-align: center;
    background-color: #0009;
    border-radius: 100%;
    transition: opacity 0.3s ease;
    z-index: 2;
  }

  &:hover .play-icon {
    opacity: 1;
  }
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const PlayerOverlay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 0 4px;
`;

const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #25de82;
  box-shadow: 0 0 8px rgba(37, 222, 130, 0.6);
`;

const PlayerText = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #94a6b5;
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

const SkeletonSlide = styled.div`
  flex-shrink: 0;
  width: calc((100% - 72px) / 7);
  aspect-ratio: 3 / 4;
  border-radius: 8px;
  background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite linear;

  @media (max-width: 1200px) { width: calc((100% - 60px) / 6); }
  @media (max-width: 1024px) { width: calc((100% - 48px) / 5); }
  @media (max-width: 900px)  { width: calc((100% - 36px) / 4); }
  @media (max-width: 768px)  { width: calc((100% - 24px) / 3.5); }
  @media (max-width: 600px)  { width: calc((100% - 24px) / 3); }
  @media (max-width: 480px)  { width: calc((100% - 12px) / 2.5); }
  @media (max-width: 360px)  { width: calc((100% - 12px) / 2.2); }
`;

const SkeletonTitleBar = styled.div`
  width: 140px;
  height: 18px;
  border-radius: 6px;
  background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;

const DynamicGroupSwiper = ({ group, prefix, searchQuery = "" }) => {
  const swiperRef = useRef(null);
  const history = useHistory();
  const slug = group.slug || group.id;
  const { data: games, isLoading } = useGameEngine({ group: slug, search: searchQuery });

  const filteredGames = React.useMemo(() => {
    if (!games) return [];
    return games; // The backend now filters by search, so we just return the games array
  }, [games]);

  const nextSlideHandler = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const prevSlideHandler = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  if (isLoading) return (
    <GamesContainer>
      <HeaderRow>
        <SkeletonTitleBar />
      </HeaderRow>
      <div style={{ display: 'flex', gap: '12px', overflow: 'hidden' }}>
        {Array.from({ length: 7 }).map((_, i) => (
          <SkeletonSlide key={i} />
        ))}
      </div>
    </GamesContainer>
  );
  if (!filteredGames || filteredGames.length === 0) return null;

  // Resolve Lucide Icon for Group Title
  const IconComponent = LucideIcons[group.icon] || Gamepad2;

  return (
    <GamesContainer>
      <HeaderRow>
        <TitleText>
          <IconComponent size={20} style={{ color: "#24A1DE" }} />
          {group.name}
        </TitleText>
        <RightControls>
          <Link to={`${prefix}${slug}`} style={{ textDecoration: 'none' }}>
            <AllButtonTopTitleContainer>Show More</AllButtonTopTitleContainer>
          </Link>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <PaginationButton onClick={prevSlideHandler}>
              <ChevronLeft size={16} />
            </PaginationButton>
            <PaginationButton onClick={nextSlideHandler}>
              <ChevronRight size={16} />
            </PaginationButton>
          </div>
        </RightControls>
      </HeaderRow>

      <Swiper
        slidesPerView={7}
        ref={swiperRef}
        spaceBetween={12}
        modules={[Autoplay, Thumbs, Mousewheel, Scrollbar]}
        loop={true}
        breakpoints={{
          1200: { slidesPerView: 7 },
          1024: { slidesPerView: 6 },
          900: { slidesPerView: 5 },
          768: { slidesPerView: 4 },
          600: { slidesPerView: 3.5 },
          480: { slidesPerView: 3 },
          360: { slidesPerView: 2.5 },
          0: { slidesPerView: 2.2 }
        }}
      >
        {filteredGames.slice(0, 30).map((game, index) => (
          <SwiperSlide key={index}>
            <CardWrapper>
              <GridItem onClick={() => history.push(`/play/${game.id}`)}>
                <img
                  src={getGameImage(game)}
                  alt={game.name}
                  className="swiper-slide-img"
                  onError={(e) => { e.target.onerror = null; e.target.src = "/assets/fallback_game.png"; }}
                />
                <div className="play-container">
                  <i className="fa fa-play play-icon"></i>
                </div>
              </GridItem>
              <PlayerOverlay>
                <ActiveDot />
                <PlayerText>
                  {(() => {
                    const seed = (game.name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                    return String(50 + (seed % 650));
                  })()} playing
                </PlayerText>
              </PlayerOverlay>
            </CardWrapper>
          </SwiperSlide>
        ))}
      </Swiper>
    </GamesContainer>
  );
};

export default DynamicGroupSwiper;
